import { createSignal, Show } from 'solid-js'
import { useNavigate, A } from '@solidjs/router'
import { CHAINS } from './lib/chains'
import { parseTxInput } from './lib/parseTx'
import TopBar from './components/TopBar'
import './App.css'

const SAMPLE_TX = '0x3ca4451e3008d523eec1c64e617663894e47cabd335654bd9f65724772682de8'
const SAMPLE_HREF = `/messages?transactionHash=${SAMPLE_TX}&domain=3`

export default function App() {
  const navigate = useNavigate()
  const [domain, setDomain] = createSignal('')
  const [txHash, setTxHash] = createSignal('')
  const [error, setError] = createSignal<string | null>(null)
  const focusOnMount = (el: HTMLInputElement) => {
    queueMicrotask(() => el.focus({ preventScroll: true }))
  }

  const submit = (e: Event) => {
    e.preventDefault()
    const raw = txHash().trim()
    if (!raw) {
      setError('Paste a transaction hash to inspect.')
      return
    }
    const parsed = parseTxInput(raw)
    if (!parsed) {
      setError("Doesn't look like a tx hash — expected 0x… (EVM) or a base58 signature (Solana).")
      return
    }
    setError(null)
    const params = new URLSearchParams({ transactionHash: parsed.tx })
    const d = domain().trim()
    if (d) {
      params.set('domain', d)
    } else if (parsed.domain != null) {
      params.set('domain', String(parsed.domain))
    }
    navigate(`/messages?${params.toString()}`)
  }

  return (
    <div class="home">
      <TopBar />

      <main class="home__main">
        <section class="hero">
          <div class="hero__eyebrow">
            <span class="dot" />
            <span>cctp v1 + v2 · iris-backed</span>
          </div>
          <h1 class="hero__tagline">
            Trace USDC <em>across chains</em>.
          </h1>

          <form class="hero__card" onSubmit={submit}>
            <label class="hero__field">
              <span class="hero__field-label">Transaction hash</span>
              <input
                ref={focusOnMount}
                type="text"
                spellcheck={false}
                autocomplete="off"
                placeholder="0x… or explorer URL"
                value={txHash()}
                autofocus
                aria-invalid={error() ? 'true' : 'false'}
                aria-describedby={error() ? 'tx-error' : undefined}
                onInput={(e) => {
                  setTxHash(e.currentTarget.value)
                  if (error()) setError(null)
                }}
              />
              <Show when={error()}>
                {(msg) => (
                  <span id="tx-error" class="hero__field-error" role="alert">
                    {msg()}
                  </span>
                )}
              </Show>
            </label>

            <div class="hero__rule" aria-hidden="true" />

            <div class="hero__domain">
              <span class="hero__domain-label">source</span>
              <div class="hero__domain-wrap">
                <select
                  class="hero__domain-select"
                  value={domain()}
                  tabindex={-1}
                  onInput={(e) => setDomain(e.currentTarget.value)}
                >
                  <option value="">auto-detect</option>
                  {Object.values(CHAINS).map((c) => (
                    <option value={String(c.domain)}>
                      {String(c.domain).padStart(2, '0')} · {c.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <button class="hero__submit" type="submit">
              <span>Inspect message</span>
              <svg
                class="hero__submit-arrow"
                viewBox="0 0 26 12"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M2 6 H22" />
                <path d="M17 1.5 L23 6 L17 10.5" />
              </svg>
            </button>
          </form>

          <A class="sample" href={SAMPLE_HREF}>
            <span class="sample__label">try a sample</span>
            <span class="sample__route">
              Arbitrum <span class="sample__arrow">──▶</span> Base
            </span>
            <span class="sample__meta">2.205 USDC · cctp-forward · complete</span>
            <span class="sample__cta" aria-hidden="true">
              <svg
                viewBox="0 0 12 12"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M3.5 8.5 L8.5 3.5" />
                <path d="M5 3.5 H8.5 V7" />
              </svg>
            </span>
          </A>
        </section>

        <section class="atlas">
          <div class="atlas__head">
            <span class="atlas__eyebrow">
              <span class="dot" />
              {Object.keys(CHAINS).length} chains · one bridge
            </span>
            <h2 class="atlas__title">Where USDC moves.</h2>
            <p class="atlas__lede">
              Each domain Circle has wired into CCTP. Burns leave from one, mints arrive at another
              — ccxp reads any pair.
            </p>
          </div>
          <ul class="atlas__list">
            {Object.values(CHAINS).map((c) => (
              <li class="atlas__row" style={{ '--accent': c.accent }}>
                <span class="atlas__id">{String(c.domain).padStart(2, '0')}</span>
                <span class="atlas__dot" aria-hidden="true" />
                <span class="atlas__name">{c.name}</span>
                <span class="atlas__short">{c.short}</span>
              </li>
            ))}
          </ul>
        </section>

        <section class="glossary">
          <div class="glossary__head">
            <span class="glossary__eyebrow">
              <span class="dot" />
              reference
            </span>
            <h2 class="glossary__title">
              A short <em>field guide</em>.
            </h2>
          </div>
          <dl class="glossary__list">
            <div class="glossary__entry">
              <dt>
                <span class="glossary__num">01</span>
                CCTP
              </dt>
              <dd>
                Circle's <b>Cross-Chain Transfer Protocol</b> — a native burn-and-mint primitive for
                USDC. The source chain destroys tokens through Circle's TokenMessenger contract, and
                the destination chain verifies the signed message and mints an equivalent amount. No
                wrapped tokens, no liquidity pool, no third-party custodian: the supply moves at the
                protocol level. Version 1 supports Standard transfers — waiting for the underlying
                chain's hard finality. Version 2 adds Fast transfers that settle in seconds and
                hooks for cross-chain composition.
              </dd>
            </div>
            <div class="glossary__entry">
              <dt>
                <span class="glossary__num">02</span>
                Iris
              </dt>
              <dd>
                Circle's offchain <b>attestation service</b>. It watches the source chain and signs
                every CCTP message once the burn clears the required finality threshold. The signed
                attestation is what unlocks the mint on the destination — without it, no USDC
                appears anywhere. Fast transfers settle the moment Circle accepts the burn risk;
                Standard waits for the underlying chain to fully finalize. ccxp reads Iris's{' '}
                <code>/v2/messages</code> endpoint directly.
              </dd>
            </div>
            <div class="glossary__entry">
              <dt>
                <span class="glossary__num">03</span>
                Domain
              </dt>
              <dd>
                CCTP's per-chain identifier — a small integer Circle assigns to each supported
                chain, distinct from the EVM chain ID. Ethereum is 0, Avalanche 1, Optimism 2,
                Arbitrum 3, Solana 5, Base 6, Polygon 7, and so on. ccxp uses the domain to query
                Iris for the source chain. If you don't specify one, the app fans out across every
                domain in parallel and uses the first hit.
              </dd>
            </div>
          </dl>
        </section>
      </main>

      <footer class="home__footer">
        <span>
          built on Circle's{' '}
          <a href="https://developers.circle.com/cctp" target="_blank" rel="noreferrer">
            Iris API ↗
          </a>
        </span>
        <span class="home__footer-sep" />
        <span>
          not affiliated with Circle. data fetched directly from <code>iris-api.circle.com</code>
        </span>
      </footer>
    </div>
  )
}
